/**
 * ========================================
 * CHATBOT VIEW CONTROLLER MODULE
 * Handles UI state transitions and view management
 * ========================================
 */
(function () {
    'use strict';

    // State
    let isOpen = false;
    let isExpanded = false;
    let isViewingResults = false;
    let welcomeVideoTimeout = null;
    window.productsVideoShownOnce = true; // Default to true so it doesn't show on page reload
    let productsVideoEligible = false;
    let lastWindowWidth = window.innerWidth;

    // Track welcome video play for this page load (resets on refresh)
    let sessionWelcomePlayed = false;
    const getHasPlayedWelcome = () => sessionWelcomePlayed;
    const setHasPlayedWelcome = () => { sessionWelcomePlayed = true; };

    // DOM Cache
    let DOM = null;

    // SVG Icons
    const ICONS = {
        VIEW_ANALYSIS: `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
              <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" fill="white"/>
            </svg>`,
        START_ANALYSIS: `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
              <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" fill="white"/>
              <path d="M20 4H16.83L15 2H9L7.17 4H4C2.9 4 2 4.9 2 6V18C2 19.1 2.9 20 4 20H20C21.1 20 22 19.1 22 18V6C22 4.9 21.1 4 20 4ZM12 17C9.24 17 7 14.76 7 12C7 9.24 9.24 7 12 7C14.76 7 17 9.24 17 12C17 14.76 14.76 17 12 17ZM20 18H4V6H8.05L9.88 4H14.12L15.95 6H20V18Z" fill="white"/>
            </svg>`,
        RETAKE_TEST: `
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" style="margin-right: 6px;">
             <path d="M17.65 6.35C16.2 4.9 14.21 4 12 4C7.58 4 4.01 7.58 4.01 12C4.01 16.42 7.58 20 12 20C15.73 20 18.84 17.45 19.73 14H17.65C16.83 16.33 14.61 18 12 18C8.69 18 6 15.31 6 12C6 8.69 8.69 6 12 6C13.66 6 15.14 6.69 16.22 7.78L13 11H20V4L17.65 6.35Z" fill="white"/>
            </svg>`,
        TOAST_ERROR: `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <circle cx="12" cy="12" r="10"></circle>
              <line x1="12" y1="8" x2="12" y2="12"></line>
              <line x1="12" y1="16" x2="12.01" y2="16"></line>
            </svg>`,
        TOAST_SUCCESS: `
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>`
    };

    const ViewController = {
        safeAutoplay: function (video, options) {
            if (!video) return;
            const opts = options || {};
            const tryUnmuteAfterStart = !!opts.tryUnmuteAfterStart;
            const startMuted = opts.startMuted !== false;
            video.dataset.chatbotStopped = '0';

            video.playsInline = true;
            video.setAttribute('playsinline', '');
            video.autoplay = true;
            video.preload = 'auto';
            if (!startMuted) {
                try { video.removeAttribute('muted'); } catch (e) { /* ignore */ }
            }

            // Cross-browser (Edge/Safari/Chrome): muted autoplay is the reliable baseline.
            video.muted = startMuted;
            const start = function () {
                if (!document.body.contains(video) || video.dataset.chatbotStopped === '1') return;
                const p = video.play();
                if (p && typeof p.then === 'function') {
                    p.then(function () {
                        if (!document.body.contains(video) || video.dataset.chatbotStopped === '1') return;
                        if (tryUnmuteAfterStart) {
                            try {
                                video.muted = false;
                                video.volume = 1;
                                const p2 = video.play();
                                if (p2 && typeof p2.catch === 'function') {
                                    p2.catch(function () {
                                        // If browser blocks unmuted playback, keep muted so at least video continues.
                                        video.muted = true;
                                    });
                                }
                            } catch (e) { /* ignore */ }
                        }
                    }).catch(function () {
                        // If unmuted autoplay is blocked, fall back to muted playback.
                        try {
                            if (video.muted !== true) {
                                video.muted = true;
                                const p3 = video.play();
                                if (p3 && typeof p3.catch === 'function') {
                                    p3.catch(function () { /* ignore */ });
                                }
                            }
                        } catch (e) { /* ignore */ }
                    });
                }
            };

            if (video.readyState >= 2) start();
            else {
                const once = function () {
                    video.removeEventListener('loadeddata', once);
                    video.removeEventListener('canplay', once);
                    start();
                };
                video.addEventListener('loadeddata', once, { once: true });
                video.addEventListener('canplay', once, { once: true });
                try { video.load(); } catch (e) { /* ignore */ }
            }
        },

        init: function () {
            if (window.ChatbotUI) {
                DOM = window.ChatbotUI.getRefs();
                this.bindEvents();
            } else {
                console.error("ChatbotUI not loaded");
            }
        },

        refreshDOM: function () {
            if (window.ChatbotUI) {
                DOM = window.ChatbotUI.getRefs();
                return DOM;
            }
        },

        getRefs: function () {
            return DOM || (window.ChatbotUI && window.ChatbotUI.getRefs());
        },

        setProductsVideoShownOnce: function(val) {
            productsVideoShownOnce = !!val;
            window.productsVideoShownOnce = !!val;
        },

        setProductsVideoEligible: function (val) {
            productsVideoEligible = !!val;
        },

        resetViewForRetake: function () {
            if (!DOM) this.refreshDOM();
            isViewingResults = false;
            window.productsVideoShownOnce = false; // Reset so fresh results show video
            this.stopAllChatbotMedia();
            if (DOM.contentContainer) {
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }
            // Explicitly hide containers to prevent products flickering while async data clears
            if (DOM.productsContainer) DOM.productsContainer.style.display = 'none';
            if (DOM.messagesContainer) DOM.messagesContainer.style.display = 'none';
            if (DOM.chatInput) DOM.chatInput.style.display = 'none';
        },

        /**
         * Intro video sits below the product cards inside #chatbot-products (same rail), above the FAQ pane.
         * No static transcript paragraph — captions come from VTT on the video.
         */
        injectResultsVideoIntoMainPanel: function () {
            if (!DOM) this.refreshDOM();
            if (!DOM.mainPanel) return;

            const videoUrl = window.PRODUCTS_VIDEO_URL || window.RESULTS_VIDEO_URL;
            console.log("[Chatbot] Injecting video into main panel. URL:", videoUrl);
            if (!videoUrl) {
                console.warn("[Chatbot] No video URL found for results intro.");
                return;
            }

            // Remove any existing video sections to prevent duplicates
            const existing = DOM.mainPanel.querySelector('.products-video-section');
            if (existing) existing.remove();

            const videoSection = document.createElement('div');
            videoSection.className = 'products-video-section';
            // Premium background and centering
            videoSection.style.cssText = 'width: 100%; height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; background: #f8fafc; position: relative; overflow: hidden; box-sizing: border-box;';

            // Container for video - removed max-width to let it fill the space as requested
            const videoCard = document.createElement('div');
            videoCard.style.cssText = 'width: 100%; height: 100%; overflow: hidden; background: #000; position: relative;';

            const video = document.createElement('video');
            video.src = videoUrl;
            video.autoplay = true;
            video.playsInline = true;
            video.muted = false;
            video.loop = false;
            video.style.cssText = 'width: 100%; height: 100%; object-fit: cover; background: #000; display: block;';

            // Sound Control (same icon style/placement across videos)
            const soundBtn = document.createElement('button');
            soundBtn.style.cssText = 'position:absolute;top:20px;right:20px;width:34px;min-width:34px;max-width:34px;height:34px;padding:0;border-radius:50%;background:rgba(239,126,25,0.95);color:#fff;border:1px solid rgba(255,255,255,0.55);box-shadow:0 4px 12px rgba(239,126,25,0.35);cursor:pointer;z-index:11;display:inline-flex;align-items:center;justify-content:center;font-size:16px;line-height:1;backdrop-filter:none;';
            soundBtn.innerHTML = '🔊';

            const syncVideoSoundLabel = () => {
                soundBtn.innerHTML = video.muted ? '🔇' : '🔊';
                soundBtn.setAttribute('aria-label', video.muted ? 'Unmute video' : 'Mute video');
            };
            syncVideoSoundLabel();

            // Explicitly trigger play to ensure it starts (prefer sound first)
            video.play().catch(() => {
                video.muted = true;
                syncVideoSoundLabel();
                const p2 = video.play();
                if (p2 && typeof p2.catch === 'function') {
                    p2.catch(err => console.warn("[Chatbot] Video play failed:", err));
                }
            });
            
            // Skip Button (moved left so mute can stay top-right)
            const skipBtn = document.createElement('button');
            skipBtn.style.cssText = 'position: absolute; top: 20px; left: 20px; background: rgba(0,0,0,0.4); backdrop-filter: blur(8px); color: #fff; border: 1px solid rgba(255,255,255,0.2); padding: 8px 16px; border-radius: 30px; font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.2s ease; z-index: 10;';
            skipBtn.textContent = 'Skip Intro';

            video.onended = () => {
                if (window._chatbotResultsVideoShown) return;
                window._chatbotResultsVideoShown = true;
                
                // Transition: fade out video, show messages
                videoSection.style.transition = 'opacity 0.6s cubic-bezier(0.4, 0, 0.2, 1)';
                videoSection.style.opacity = '0';
                setTimeout(() => {
                    videoSection.remove();
                    DOM.messagesContainer.style.display = 'flex';
                    DOM.messagesContainer.style.opacity = '0';
                    DOM.messagesContainer.style.transition = 'opacity 0.6s ease';
                    requestAnimationFrame(() => { DOM.messagesContainer.style.opacity = '1'; });
                    
                    if (typeof window._chatbotResultsVideoOnEnded === 'function') {
                        window._chatbotResultsVideoOnEnded();
                    }
                }, 600);
            };

            soundBtn.onclick = (e) => {
                e.stopPropagation();
                video.muted = !video.muted;
                if (!video.muted) {
                    video.volume = 1;
                }
                syncVideoSoundLabel();
            };

            skipBtn.onclick = (e) => {
                e.stopPropagation();
                video.onended();
            };

            video.onclick = () => {
                if (video.paused) video.play();
                else {
                    // Toggle sound on tap if muted
                    if (video.muted) {
                        video.muted = false;
                        syncVideoSoundLabel();
                    }
                }
            };

            videoCard.appendChild(video);
            videoCard.appendChild(soundBtn);
            videoCard.appendChild(skipBtn);
            videoSection.appendChild(videoCard);
            
            // Clear main panel and add video
            DOM.messagesContainer.style.display = 'none';
            DOM.mainPanel.appendChild(videoSection);
        },

        injectProductsIntroMedia: function () {
            // Deprecated - replaced by injectResultsVideoIntoMainPanel
            return;
        },

        stopWelcomeVideo: function () {
            if (!DOM) this.refreshDOM();
            if (welcomeVideoTimeout) {
                clearTimeout(welcomeVideoTimeout);
                welcomeVideoTimeout = null;
            }
            if (DOM && DOM.welcomeScreen) {
                const video = DOM.welcomeScreen.querySelector('.welcome-video-element');
                if (video) {
                    video.pause();
                    video.muted = true;
                    video.currentTime = 0;
                }
            }
        },

        /** Stop all audio and video in the chatbot (on page change, tab switch, or view switch) */
        stopAllChatbotMedia: function () {
            const box = document.getElementById('chatbot-box');
            if (!box) return;
            box.querySelectorAll('video').forEach(function (v) {
                try {
                    v.dataset.chatbotStopped = '1';
                    v.pause();
                    v.muted = true;
                    v.currentTime = 0;
                } catch (e) { /* ignore */ }
            });
            box.querySelectorAll('audio').forEach(function (a) {
                try {
                    a.pause();
                    a.currentTime = 0;
                } catch (e) { /* ignore */ }
            });
        },

        bindEvents: function () {
            if (!DOM) return;
            var self = this;
            document.addEventListener('visibilitychange', function () {
                if (document.hidden) self.stopAllChatbotMedia();
            });
            window.addEventListener('pagehide', function () { self.stopAllChatbotMedia(); });
            window.addEventListener('beforeunload', function () { self.stopAllChatbotMedia(); });
            window.addEventListener('popstate', function () { self.stopAllChatbotMedia(); });
            window.addEventListener('hashchange', function () { self.stopAllChatbotMedia(); });
        },

        toggleChatbot: function (forceState = null) {
            if (!DOM) this.refreshDOM();
            if (!DOM) return;

            isOpen = forceState !== null ? forceState : !isOpen;
            DOM.box.classList.toggle('open', isOpen);
            if (DOM.toggle) {
                // On dedicated chatbot page, keep navbar button state untouched.
                if (!window.CHATBOT_PAGE_MODE) {
                    DOM.toggle.classList.toggle('hidden', isOpen);
                }
            }
            if (!isOpen) this.stopAllChatbotMedia();
            // When reopening, re-sync cart counts so the UI doesn't look stale.
            if (isOpen && window.ChatbotProducts && typeof window.ChatbotProducts.syncStorefrontCartCount === 'function') {
                // Delay slightly so product cards (DOM) are present after view toggle.
                requestAnimationFrame(() => {
                    setTimeout(() => {
                        try { window.ChatbotProducts.syncStorefrontCartCount(); } catch (e) { /* ignore */ }
                    }, 250);
                });
            }
            return isOpen;
        },

        toggleExpand: function (forceState = null, shouldScroll = true) {
            if (!DOM) this.refreshDOM();
            // Expanded/full-width mode is intentionally disabled.
            isExpanded = false;
            if (DOM && DOM.box) DOM.box.classList.remove('expanded');
            if (DOM && DOM.expand) DOM.expand.innerHTML = '⛶';

            // CRITICAL FIX: If we are in "Guided Chat" or "Results" view, we must re-calculate the layout
            // immediately because our split logic depends on the 'expanded' class we just toggled.
            if (isViewingResults) {
                // Layout refresh only – don't stop products video (skipMediaStop = true)
                this.showGuidedChatView(true);
            }

            if (shouldScroll && DOM.messagesContainer) {
                setTimeout(() => {
                    DOM.messagesContainer.scrollTo({
                        top: DOM.messagesContainer.scrollHeight,
                        behavior: 'smooth'
                    });
                }, 350);
            }

            // RE-RENDER PRODUCTS ON EXPAND/COLLAPSE TO MATCH LAYOUT
            if (isViewingResults && window.ChatbotProducts && typeof window.ChatbotProducts.refreshDisplay === 'function') {
                window.ChatbotProducts.refreshDisplay();
            }

            return false;
        },

        showWelcomeScreen: function ({ hasRecommendations, canShowQuestionnaire, onStart, onView, onRetake }) {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();

            // Visibility: Hide everything else and show welcome
            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.style.overflowY = 'hidden';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }

            DOM.welcomeScreen.style.display = 'flex';
            DOM.questionnaireContainer.style.display = 'none';
            DOM.faceScanContainer.style.display = 'none';
            if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.clearFaceScanFlowMarker) {
                ChatbotFaceScan.clearFaceScanFlowMarker();
            }
            DOM.productsContainer.style.display = 'none';
            DOM.messagesContainer.style.display = 'none';
            DOM.chatInput.style.display = 'none';

            // Clear header progress/icons from results page
            if (window.ChatbotProducts && typeof window.ChatbotProducts.hideProducts === 'function') {
                window.ChatbotProducts.hideProducts();
            }
            const topHeaderTitle = document.querySelector('#chatbot-header-title span');
            if (topHeaderTitle) {
                topHeaderTitle.textContent = 'Codeskin';
                topHeaderTitle.style.visibility = 'visible';
            }

            // Play welcome video once from start - UNMUTED; VTT text shows in line below video (not on video)
            const welcomeVideo = DOM.welcomeScreen.querySelector('#welcome-video-player');
            const welcomeCaptionLine = DOM.welcomeScreen.querySelector('#welcome-video-captions-line');
            const welcomeVideoMuteToggle = DOM.welcomeScreen.querySelector('#welcome-video-mute-toggle');
            const welcomeCaptionsFallback = 'Welcome to CodeSkin. I am Cody, your AI assistant.';

            if (welcomeVideo) {
                var bindWelcomeCaptionsBelowVideo = function () {
                    if (!welcomeCaptionLine || welcomeVideo.dataset.chatbotWelcomeCaptionsInit === '1') return;
                    welcomeVideo.dataset.chatbotWelcomeCaptionsInit = '1';

                    var updateLiveCaptions = function () {
                        var text = '';
                        try {
                            var tracks = welcomeVideo.textTracks;
                            if (!tracks || !tracks.length) {
                                if (!window.WELCOME_VIDEO_CAPTIONS_URL) {
                                    welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                                }
                                return;
                            }
                            for (var i = 0; i < tracks.length; i++) {
                                var tr = tracks[i];
                                if (tr.kind !== 'captions' && tr.kind !== 'subtitles') continue;
                                var cues = tr.activeCues;
                                if (!cues || !cues.length) continue;
                                for (var j = 0; j < cues.length; j++) {
                                    var c = cues[j];
                                    if (c && c.text) text += c.text.replace(/\s+/g, ' ').trim() + ' ';
                                }
                            }
                        } catch (err) { /* ignore */ }
                        welcomeCaptionLine.textContent = text.trim();
                    };

                    var captionCueListenersBound = false;
                    var bindCaptionTracks = function () {
                        try {
                            var tracks = welcomeVideo.textTracks;
                            if (!tracks || !tracks.length) {
                                if (!window.WELCOME_VIDEO_CAPTIONS_URL) {
                                    welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                                }
                                updateLiveCaptions();
                                return;
                            }
                            if (!captionCueListenersBound) {
                                for (var k = 0; k < tracks.length; k++) {
                                    var tr = tracks[k];
                                    if (tr.kind !== 'captions' && tr.kind !== 'subtitles') continue;
                                    tr.addEventListener('cuechange', updateLiveCaptions);
                                }
                                captionCueListenersBound = true;
                            }
                            for (var k2 = 0; k2 < tracks.length; k2++) {
                                var tr2 = tracks[k2];
                                if (tr2.kind === 'captions' || tr2.kind === 'subtitles') tr2.mode = 'hidden';
                            }
                        } catch (e) { /* ignore */ }
                        updateLiveCaptions();
                    };

                    welcomeVideo.addEventListener('timeupdate', updateLiveCaptions);
                    welcomeVideo.addEventListener('loadedmetadata', bindCaptionTracks);
                    welcomeVideo.addEventListener('canplay', bindCaptionTracks);
                    welcomeVideo.querySelectorAll('track').forEach(function (t) {
                        t.addEventListener('load', bindCaptionTracks);
                    });
                    setTimeout(bindCaptionTracks, 300);
                };

                bindWelcomeCaptionsBelowVideo();

                const syncWelcomeMuteLabel = function () {
                    if (!welcomeVideoMuteToggle) return;
                    if (welcomeVideo.muted) {
                        welcomeVideoMuteToggle.textContent = '🔇';
                        welcomeVideoMuteToggle.setAttribute('aria-label', 'Unmute welcome video');
                    } else {
                        welcomeVideoMuteToggle.textContent = '🔊';
                        welcomeVideoMuteToggle.setAttribute('aria-label', 'Mute welcome video');
                    }
                };
                if (welcomeVideoMuteToggle && !welcomeVideoMuteToggle.dataset.bound) {
                    welcomeVideoMuteToggle.dataset.bound = '1';
                    welcomeVideoMuteToggle.onclick = function () {
                        try {
                            welcomeVideo.muted = !welcomeVideo.muted;
                            if (!welcomeVideo.muted) welcomeVideo.volume = 1;
                            const p = welcomeVideo.play();
                            if (p && typeof p.catch === 'function') p.catch(function () { /* ignore */ });
                        } catch (e) { /* ignore */ }
                        syncWelcomeMuteLabel();
                    };
                }
                if (welcomeVideoMuteToggle) {
                    var welcomeHost = welcomeVideo.parentElement || DOM.welcomeScreen;
                    if (welcomeHost && welcomeHost.style) welcomeHost.style.position = 'relative';
                    welcomeVideoMuteToggle.style.position = 'absolute';
                    welcomeVideoMuteToggle.style.top = '8px';
                    welcomeVideoMuteToggle.style.right = '8px';
                    welcomeVideoMuteToggle.style.left = 'auto';
                    welcomeVideoMuteToggle.style.margin = '0';
                    welcomeVideoMuteToggle.style.width = '34px';
                    welcomeVideoMuteToggle.style.minWidth = '34px';
                    welcomeVideoMuteToggle.style.maxWidth = '34px';
                    welcomeVideoMuteToggle.style.height = '34px';
                    welcomeVideoMuteToggle.style.padding = '0';
                    welcomeVideoMuteToggle.style.borderRadius = '50%';
                    welcomeVideoMuteToggle.style.zIndex = '12';
                    welcomeVideoMuteToggle.style.display = 'inline-flex';
                    welcomeVideoMuteToggle.style.alignItems = 'center';
                    welcomeVideoMuteToggle.style.justifyContent = 'center';
                    welcomeVideoMuteToggle.style.fontSize = '16px';
                    welcomeVideoMuteToggle.style.lineHeight = '1';
                    welcomeVideoMuteToggle.style.background = 'rgba(239,126,25,0.95)';
                    welcomeVideoMuteToggle.style.color = '#fff';
                    welcomeVideoMuteToggle.style.border = '1px solid rgba(255,255,255,0.55)';
                    welcomeVideoMuteToggle.style.boxShadow = '0 4px 12px rgba(239,126,25,0.35)';
                    welcomeVideoMuteToggle.style.backdropFilter = 'none';
                }
                welcomeVideo.addEventListener('volumechange', syncWelcomeMuteLabel);
                welcomeVideo.addEventListener('play', syncWelcomeMuteLabel);

                if (!getHasPlayedWelcome()) {
                    if (welcomeVideoMuteToggle) welcomeVideoMuteToggle.style.display = 'inline-flex';
                    if (welcomeVideoTimeout) clearTimeout(welcomeVideoTimeout);
                    welcomeVideoTimeout = setTimeout(() => {
                        console.log("[ViewController] Initializing welcome video playback...");

                        const attemptPlay = () => {
                            welcomeVideo.currentTime = 0;
                            welcomeVideo.loop = false;
                            welcomeVideo.defaultMuted = false;
                            welcomeVideo.removeAttribute('muted');
                            welcomeVideo.muted = false;
                            welcomeVideo.volume = 1;
                            welcomeVideo.onended = function () {
                                this.pause();
                                if (welcomeCaptionLine) {
                                    welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                                }
                            };
                            this.safeAutoplay(welcomeVideo, {
                                tryUnmuteAfterStart: true,
                                startMuted: false
                            });
                            syncWelcomeMuteLabel();

                            // On dedicated page, unmute on first user interaction if autoplay policy muted it.
                            if (window.CHATBOT_PAGE_MODE) {
                                const unmuteOnGesture = function () {
                                    try {
                                        welcomeVideo.removeAttribute('muted');
                                        welcomeVideo.defaultMuted = false;
                                        welcomeVideo.muted = false;
                                        welcomeVideo.volume = 1;
                                        const p4 = welcomeVideo.play();
                                        if (p4 && typeof p4.catch === 'function') p4.catch(function () { /* ignore */ });
                                    } catch (e) { /* ignore */ }
                                    document.removeEventListener('click', unmuteOnGesture, true);
                                    document.removeEventListener('touchstart', unmuteOnGesture, true);
                                    document.removeEventListener('keydown', unmuteOnGesture, true);
                                    document.removeEventListener('pointerdown', unmuteOnGesture, true);
                                    document.removeEventListener('wheel', unmuteOnGesture, true);
                                };
                                document.addEventListener('click', unmuteOnGesture, true);
                                document.addEventListener('touchstart', unmuteOnGesture, true);
                                document.addEventListener('keydown', unmuteOnGesture, true);
                                document.addEventListener('pointerdown', unmuteOnGesture, true);
                                document.addEventListener('wheel', unmuteOnGesture, true);

                                // Tapping video itself should always try enabling voice.
                                welcomeVideo.addEventListener('click', unmuteOnGesture, { once: true, capture: true });
                                welcomeVideo.addEventListener('touchstart', unmuteOnGesture, { once: true, capture: true });

                                // Retry unmute a few times; some browsers allow this after playback stabilizes.
                                let tries = 0;
                                const retryUnmute = () => {
                                    tries += 1;
                                    if (!document.body.contains(welcomeVideo) || welcomeVideo.dataset.chatbotStopped === '1') return;
                                    try {
                                        welcomeVideo.removeAttribute('muted');
                                        welcomeVideo.defaultMuted = false;
                                        welcomeVideo.muted = false;
                                        welcomeVideo.volume = 1;
                                        const p5 = welcomeVideo.play();
                                        if (p5 && typeof p5.catch === 'function') p5.catch(function () { /* ignore */ });
                                    } catch (e) { /* ignore */ }
                                    if (!welcomeVideo.muted || tries >= 4) return;
                                    setTimeout(retryUnmute, 700);
                                };
                                setTimeout(retryUnmute, 300);
                            }
                            setHasPlayedWelcome();
                        };

                        // Ensure video is ready to play
                        if (welcomeVideo.readyState >= 3) {
                            attemptPlay();
                        } else {
                            welcomeVideo.oncanplay = () => {
                                welcomeVideo.oncanplay = null;
                                attemptPlay();
                            };
                            welcomeVideo.load();
                        }
                    }, 300);
                } else {
                    welcomeVideo.pause();
                    if (welcomeVideoMuteToggle) welcomeVideoMuteToggle.style.display = 'none';
                    if (!window.CHATBOT_PAGE_MODE) {
                        welcomeVideo.style.display = 'none'; // Also hide it once played to move text up
                    }
                    if (welcomeCaptionLine) {
                        welcomeCaptionLine.textContent = welcomeCaptionsFallback;
                    }
                }
            } else if (welcomeCaptionLine && !welcomeCaptionLine.textContent.trim()) {
                welcomeCaptionLine.textContent = welcomeCaptionsFallback;
            }

            // Bind clicks
            const self = this;
            if (onStart) DOM.startQuestionnaireBtn.onclick = () => { 
                self.stopWelcomeVideo(); 
                window.productsVideoShownOnce = false; // Fresh test starts, allow video on results
                onStart(); 
            };
            if (onView) DOM.viewAnalysisBtn.onclick = () => { self.stopWelcomeVideo(); onView(); };
            if (onRetake) DOM.retakeTestWelcomeBtn.onclick = () => { 
                self.stopWelcomeVideo(); 
                window.productsVideoShownOnce = false; // Retake starts, allow video on results
                onRetake(); 
            };

            if (hasRecommendations || !canShowQuestionnaire) {
                DOM.startQuestionnaireBtn.style.display = 'none';
                DOM.viewAnalysisBtn.style.display = 'flex';
                DOM.retakeTestWelcomeBtn.style.display = 'flex';
            } else {
                DOM.startQuestionnaireBtn.style.display = 'flex';
                DOM.viewAnalysisBtn.style.display = 'none';
                DOM.retakeTestWelcomeBtn.style.display = 'none';
            }
        },

        toggleResultsView: function (hasRecommendations) {
            if (!DOM) this.refreshDOM();

            if (!hasRecommendations && (!DOM.productsContainer || DOM.productsContainer.innerHTML.trim() === '')) {
                return;
            }

            // If we have recommendations coming but the container is empty, show spinner
            if (hasRecommendations && (!DOM.productsContainer || DOM.productsContainer.innerHTML.trim() === '')) {
                this.showSpinner('Preparing your results...', 60);
            }

            isViewingResults = true;
            const isWideScreen = window.innerWidth >= 768;

            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.style.overflowY = 'hidden';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.add('show-results'); // ADD THIS
            }

            if (isExpanded && isWideScreen) {
                // Wide Screen Split View
                this.showGuidedChatView();
            } else {
                // Mobile/Compressed: Show both (Unified Split View)
                this.showGuidedChatView();
            }

            if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.textContent = 'Back to Chat';
        },

        toggleChatView: function () {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();
            isViewingResults = false;

            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.style.overflowY = 'hidden';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }

            DOM.mainPanel.style.display = 'flex';
            DOM.mainPanel.style.width = '100%'; // Ensure full width
            DOM.mainPanel.style.height = '100%';
            DOM.mainPanel.style.flex = '1';
            DOM.mainPanel.style.order = '1';

            DOM.welcomeScreen.style.display = 'none';
            DOM.questionnaireContainer.style.display = 'none';
            DOM.faceScanContainer.style.display = 'none';
            if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.clearFaceScanFlowMarker) {
                ChatbotFaceScan.clearFaceScanFlowMarker();
            }
            DOM.messagesContainer.style.display = 'flex';
            DOM.messagesContainer.style.height = '100%';
            DOM.chatInput.style.display = 'none';
            DOM.productsContainer.style.display = 'none';

            if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.textContent = 'View Results';

            setTimeout(() => {
                DOM.messagesContainer.scrollTo({ top: DOM.messagesContainer.scrollHeight, behavior: 'auto' });
            }, 50);
        },

        showGuidedChatView: function (productData, skipMediaStop) {
            if (!DOM) this.refreshDOM();
            if (!skipMediaStop) this.stopAllChatbotMedia();
            isViewingResults = true;

            // Enable split view ONLY if screen is wide enough AND the widget is explicitly expanded
            const isWideEnough = window.innerWidth >= 768; // Lowered from 1024 for better compatibility

            // Re-read expanded state directly from DOM to ensure accuracy
            const box = document.getElementById('chatbot-box');
            const isMsgExpanded = box ? box.classList.contains('expanded') : false;

            // Keep dedicated chatbot page in stacked flow (products first, FAQ below).
            // Split view is only for expanded popup widget on wide screens.
            const shouldSplit = isMsgExpanded && isWideEnough;

            // 1. CONTAINER STYLE
            if (DOM.contentContainer) {
                DOM.contentContainer.style.display = 'flex';
                DOM.contentContainer.classList.add('guided-mode-active');
                DOM.contentContainer.style.background = '#ffffff';
                DOM.contentContainer.style.height = window.CHATBOT_PAGE_MODE ? 'auto' : '100%';

                if (shouldSplit) {
                    // WIDE SCREEN SPLIT: Left Chat (70%), Right Products (30%)
                    DOM.contentContainer.style.flexDirection = 'row';
                    DOM.contentContainer.style.overflow = 'hidden';
                } else {
                    // Stacked layout: outer column does not scroll — products rail + main panel scroll inside
                    DOM.contentContainer.style.flexDirection = 'column';
                    DOM.contentContainer.style.overflowY = 'hidden';
                    DOM.contentContainer.style.overflowX = 'hidden';
                }
            }

            // 2. MAIN PANEL (LEFT SIDE)
            if (DOM.mainPanel) {
                DOM.mainPanel.style.display = 'flex';
                DOM.mainPanel.style.background = '#ffffff';
                DOM.mainPanel.style.flexDirection = 'column';
                DOM.mainPanel.style.minHeight = '0';

                if (shouldSplit) {
                    DOM.mainPanel.style.flex = '0 0 70%';
                    DOM.mainPanel.style.width = '70%';
                    DOM.mainPanel.style.height = '100%';
                    DOM.mainPanel.style.overflowY = 'auto';
                    DOM.mainPanel.style.borderRight = '1px solid #e2e8f0';
                    DOM.mainPanel.style.order = '1'; // Left
                } else {
                    DOM.mainPanel.style.flex = '0 0 auto';
                    DOM.mainPanel.style.width = '100%';
                    DOM.mainPanel.style.height = 'auto';
                    DOM.mainPanel.style.minHeight = '0';
                    DOM.mainPanel.style.overflowY = 'auto';
                    DOM.mainPanel.style.borderRight = 'none';
                    DOM.mainPanel.style.order = '2';
                    DOM.mainPanel.style.borderTop = 'none';
                }

                // TOGGLE VIDEO VS CHAT
                const existingVideo = DOM.mainPanel.querySelector('.products-video-section');
                
                // CRITICAL: Always show messages instead of the large video intro 
                // because we now have the video card inside the products carousel.
                if (existingVideo) existingVideo.remove();
                DOM.messagesContainer.style.display = 'flex';
                // Input visibility depends on guided activity (handled in ChatbotMessages)
                window._chatbotResultsVideoShown = true; // Mark as shown to prevent other logic from trying to inject it

                DOM.mainPanel.style.zIndex = '5';

                // CRITICAL: Ensure we don't hide the children we actually need (messages)
                // Hiding other siblings
                DOM.welcomeScreen.style.display = 'none';
                DOM.questionnaireContainer.style.display = 'none';
                DOM.faceScanContainer.style.display = 'none';
                if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.clearFaceScanFlowMarker) {
                    ChatbotFaceScan.clearFaceScanFlowMarker();
                }
            }

            // 3. PRODUCTS AREA
            if (DOM.productsContainer) {
                DOM.productsContainer.style.display = 'block';
                DOM.productsContainer.style.background = shouldSplit ? '#f8fafc' : '#ffffff';

                if (shouldSplit) {
                    DOM.productsContainer.style.width = '30%';
                    DOM.productsContainer.style.flex = '0 0 30%';
                    DOM.productsContainer.style.height = '100%';
                    DOM.productsContainer.style.overflowY = 'auto';
                    DOM.productsContainer.style.padding = '0'; // Removed gap at top
                    DOM.productsContainer.style.borderLeft = 'none';
                    DOM.productsContainer.style.order = '2'; // Right
                } else {
                    /* Stacked: video + cards = content-sized; FAQ pane takes remaining flex space */
                    DOM.productsContainer.style.width = '100%';
                    DOM.productsContainer.style.flex = '0 1 auto';
                    DOM.productsContainer.style.height = 'auto';
                    DOM.productsContainer.style.minHeight = '0';
                    DOM.productsContainer.style.maxHeight = 'none';
                    DOM.productsContainer.style.overflowY = 'auto';
                    DOM.productsContainer.style.overflowX = 'hidden';
                    DOM.productsContainer.style.padding = '0';
                    DOM.productsContainer.style.order = '1';
                    DOM.productsContainer.style.background = '#ffffff';
                    DOM.productsContainer.style.borderBottom = 'none';
                }

                const grids = DOM.productsContainer.querySelectorAll('.products-grid');
                grids.forEach(grid => {
                    if (shouldSplit) {
                        // Wide Screen Sidebar: Stack categories, but keep horizontal scroll for products inside if they don't fit
                        // Actually, we don't need to force column here as categories already stack
                        // But let's ensure the grid doesn't force a wrap that might look bad in a narrow sidebar
                        grid.style.flexWrap = 'nowrap';
                    } else {
                        // Mobile: Standard layout
                        grid.style.flexWrap = 'nowrap';
                    }
                });

                // Clear any inline styles periodically added to cards to ensure they follow CSS
                const cards = DOM.productsContainer.querySelectorAll('.product-card');
                cards.forEach(card => {
                    card.style.width = '';
                    card.style.minWidth = '';
                    card.style.maxWidth = '';
                    card.style.marginBottom = '';

                    const imgCont = card.querySelector('.product-image-container');
                    if (imgCont) imgCont.style.height = '';

                    const desc = card.querySelector('.product-description');
                    if (desc) desc.style.display = '';
                });

                // Ensure the header is visible as it contains primary action buttons (Retake, Add All, etc)
                const header = DOM.productsContainer.querySelector('.products-header');
                if (header) header.style.display = 'block';
            }

            // 4. MESSAGES AREA — FAQ / guided chat (intro video lives in #chatbot-products)
            if (DOM.messagesContainer) {
                DOM.messagesContainer.style.display = 'flex';
                DOM.messagesContainer.style.flexDirection = 'column';
                DOM.messagesContainer.style.background = '#ffffff';
                if (shouldSplit) {
                    DOM.messagesContainer.style.height = '100%';
                    DOM.messagesContainer.style.flex = '1 1 0';
                    DOM.messagesContainer.style.minHeight = '0';
                    DOM.messagesContainer.style.overflowY = 'auto';
                    DOM.messagesContainer.style.padding = '10px 12px 14px';
                } else {
                    DOM.messagesContainer.style.height = 'auto';
                    DOM.messagesContainer.style.padding = '12px 14px 10px';
                    if (window.CHATBOT_PAGE_MODE) {
                        // Dedicated page: FAQ should flow naturally with significantly more space.
                        DOM.messagesContainer.style.flex = '0 1 auto';
                        DOM.messagesContainer.style.minHeight = '300px';
                        DOM.messagesContainer.style.maxHeight = '800px';
                        DOM.messagesContainer.style.overflowY = 'auto';
                    } else {
                        DOM.messagesContainer.style.flex = '1 1 auto';
                        DOM.messagesContainer.style.minHeight = '300px';
                        DOM.messagesContainer.style.maxHeight = '650px';
                        DOM.messagesContainer.style.overflowY = 'auto';
                    }
                }

                var skinDropdownOpen = !!(DOM.contentContainer && DOM.contentContainer.classList.contains('skin-dropdown-open'));
                // Keep intro video visible only on products page state.
                // Never play it while Skin Profile / Face Scan panel is open.
                var hasGuidedContent = !!DOM.messagesContainer.querySelector('.quick-replies-container');
                if (hasGuidedContent || skinDropdownOpen) {
                    document.querySelectorAll('.chatbot-products-media-stack').forEach(function (node) {
                        var v = node.querySelector('video');
                        if (v) {
                            try {
                                v.pause();
                                v.currentTime = 0;
                                v.muted = true;
                            } catch (e) { /* ignore */ }
                        }
                        if (node && node.parentNode) node.parentNode.removeChild(node);
                    });
                    document.querySelectorAll('#chatbot-messages .products-video-section').forEach(function (node) {
                        if (node && node.parentNode) node.parentNode.removeChild(node);
                    });
                } else {
                }
            }

            if (DOM.chatInput) DOM.chatInput.style.display = 'none';
            if (DOM.viewResultsHeaderBtn) DOM.viewResultsHeaderBtn.textContent = 'Back to Results';

            // SNAPPY LAYOUT REFRESH
            requestAnimationFrame(() => {
                if (shouldSplit) {
                    DOM.mainPanel.scrollTop = 0;
                } else {
                    // Keep FAQ area anchored at the start in stacked/page flow.
                    if (DOM.mainPanel) DOM.mainPanel.scrollTop = 0;
                    if (DOM.messagesContainer) DOM.messagesContainer.scrollTop = 0;
                }
            });
        },

        showFaceScan: function () {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();
            isViewingResults = false;

            // Hide other views
            DOM.welcomeScreen.style.display = 'none';
            DOM.questionnaireContainer.style.display = 'none';
            DOM.messagesContainer.style.display = 'none';
            DOM.productsContainer.style.display = 'none';
            DOM.chatInput.style.display = 'none';

            // Clear header progress/icons from results page
            if (window.ChatbotProducts && typeof window.ChatbotProducts.hideProducts === 'function') {
                window.ChatbotProducts.hideProducts();
            }
            const topHeaderTitle = document.querySelector('#chatbot-header-title span');
            if (topHeaderTitle) {
                topHeaderTitle.textContent = 'Codeskin';
                topHeaderTitle.style.visibility = 'visible';
            }

            // Show main panel and face scan
            DOM.mainPanel.style.display = 'flex';
            DOM.faceScanContainer.style.display = 'flex';

            // Reset panel layout
            if (DOM.contentContainer) {
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');
            }
            if (DOM.mainPanel) {
                DOM.mainPanel.style.width = '100%';
                DOM.mainPanel.style.flex = '1';
                DOM.mainPanel.style.height = '100%';
            }
        },

        resetViewForRetake: function () {
            if (!DOM) this.refreshDOM();
            this.stopAllChatbotMedia();
            isViewingResults = false;
            window.productsVideoShownOnce = false; // CRITICAL: Reset video flag for retake
            productsVideoEligible = false;
            
            if (DOM.productsContainer) {
                DOM.productsContainer.style.display = 'none';
                DOM.productsContainer.innerHTML = '';
            }

            // RESET LAYOUT from Split View
            if (DOM.contentContainer) {
                const isBoxExpanded = document.getElementById('chatbot-box')?.classList.contains('expanded');
                DOM.contentContainer.style.flexDirection = 'column';
                DOM.contentContainer.classList.remove('guided-mode-active');
                DOM.contentContainer.classList.remove('show-results');

                // If expanded, ensure it takes full space correctly
                if (isBoxExpanded) {
                    DOM.contentContainer.style.width = '100%';
                    DOM.contentContainer.style.height = '100%';
                }
            }
            if (DOM.mainPanel) {
                DOM.mainPanel.style.width = '100%';
                DOM.mainPanel.style.flex = '1';
                DOM.mainPanel.style.borderRight = 'none';
                DOM.mainPanel.style.maxWidth = 'none';
                DOM.mainPanel.style.height = '100%';
                DOM.mainPanel.style.overflowY = 'hidden'; /* Sticky Nav Fix: Lock parent, scroll content */
                DOM.mainPanel.style.order = '1';
            }

            DOM.welcomeScreen.style.display = 'none';
            DOM.faceScanContainer.style.display = 'none';
            if (typeof ChatbotFaceScan !== 'undefined' && ChatbotFaceScan.clearFaceScanFlowMarker) {
                ChatbotFaceScan.clearFaceScanFlowMarker();
            }
            DOM.messagesContainer.style.display = 'none';
            DOM.chatInput.style.display = 'none';
            DOM.mainPanel.style.display = 'flex';
            DOM.questionnaireContainer.style.display = 'none';

            // Clear header progress/icons from results page
            if (window.ChatbotProducts && typeof window.ChatbotProducts.hideProducts === 'function') {
                window.ChatbotProducts.hideProducts();
            }
            const topHeaderTitle = document.querySelector('#chatbot-header-title span');
            if (topHeaderTitle) {
                topHeaderTitle.textContent = 'Codeskin';
                topHeaderTitle.style.visibility = 'visible';
            }

            // Clear content but keep container
            if (DOM.questionnaireContent) DOM.questionnaireContent.innerHTML = '';

            if (DOM.viewResultsHeaderBtn) {
                DOM.viewResultsHeaderBtn.textContent = 'View Results';
                DOM.viewResultsHeaderBtn.style.display = 'none';
            }
        },

        showSpinner: function (message = 'Loading...', padding = 40) {
            return `
                <div style="text-align:center; padding: ${padding}px;">
                    <div style="width: 40px; height: 40px; border: 3px solid #f3f3f3; border-top: 3px solid rgba(239, 126, 25, 1); border-radius: 50%; animation: spin 0.8s linear infinite; margin: 0 auto;"></div>
                    <p style="margin-top:10px; color:#64748b;">${message}</p>
                    <style>@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }</style>
                </div>`;
        },

        showProgressLoader: function (message = 'Analyzing your skin profile...') {
            const uniqueId = 'loader_' + Date.now();
            const resultsVideoUrl = window.RESULTS_VIDEO_URL || '';

            return `
                <div id="${uniqueId}" style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%; padding: 40px 40px; text-align: center;">
                    <h3 style="font-size: 18px; color: #1e293b; margin-bottom: 24px; font-weight: 600;">${message}</h3>
                    <div style="width: 100%; max-width: 260px; background: #e2e8f0; border-radius: 20px; height: 12px; overflow: hidden; position: relative; box-shadow: inset 0 1px 3px rgba(0,0,0,0.1);">
                        <div id="${uniqueId}_bar" class="js-progress-bar" style="height: 100%; background: rgba(239, 126, 25, 1); border-radius: 20px; width: 0%; transition: width 0.3s ease-out;"></div>
                    </div>
                    <div style="margin-top: 12px; font-size: 16px; font-weight: 700; color: rgba(239, 126, 25, 1);">
                        <span id="${uniqueId}_percent" class="js-progress-text">0</span>%
                    </div>
                    <p style="margin-top: 12px; color: #94a3b8; font-size: 12px;">Please wait while we generate your personalized routine...</p>
                    <div style="flex-shrink: 0; width: 100%; max-width: 320px; border-radius: 12px; margin-top: 12px; display: block; position: relative;">
                        <video playsinline style="width: 100%; max-width: 320px; height: auto; object-fit: contain; background: transparent; display: block; margin: 0 auto;">
                            <source src="${resultsVideoUrl}" type="video/mp4">
                        </video>
                        <button type="button" class="js-loader-video-mute" style="position:absolute; top:8px; right:8px; width:34px; min-width:34px; max-width:34px; height:34px; padding:0; background:rgba(239,126,25,0.95); color:#fff; border:1px solid rgba(255,255,255,0.55); border-radius:50%; box-shadow:0 4px 12px rgba(239,126,25,0.35); cursor:pointer; display:inline-flex; align-items:center; justify-content:center; font-size:16px; line-height:1; z-index:12;">🔊</button>
                    </div>
                </div>`;
        },

        startProgressAnimation: function (container) {
            if (!container) return;
            const bar = container.querySelector('.js-progress-bar');
            const text = container.querySelector('.js-progress-text');
            const video = container.querySelector('video');
            const muteBtn = container.querySelector('.js-loader-video-mute');

            // Attempt unmuted playback for the analysis video
            if (video) {
                video.onended = function () { this.currentTime = 0; this.pause(); };
                const syncLoaderMuteLabel = function () {
                    if (!muteBtn) return;
                    muteBtn.textContent = video.muted ? '🔇' : '🔊';
                    muteBtn.setAttribute('aria-label', video.muted ? 'Unmute loading video' : 'Mute loading video');
                };
                if (muteBtn) {
                    muteBtn.onclick = function (e) {
                        e.preventDefault();
                        e.stopPropagation();
                        video.muted = !video.muted;
                        if (!video.muted) video.volume = 1;
                        const p = video.play();
                        if (p && typeof p.catch === 'function') p.catch(function () { /* ignore */ });
                        syncLoaderMuteLabel();
                    };
                }
                video.addEventListener('volumechange', syncLoaderMuteLabel);
                this.safeAutoplay(video, { tryUnmuteAfterStart: true, startMuted: false });
                syncLoaderMuteLabel();
            }

            if (!bar) return;

            let progress = 0;
            // Clear any existing interval if we attach it to the element (optional safety)
            if (container._progressInterval) clearInterval(container._progressInterval);

            container._progressInterval = setInterval(() => {
                if (progress >= 100) {
                    clearInterval(container._progressInterval);
                } else {
                    // ENGAGING AI SIMULATION (Approx 5 Seconds Total)
                    // limit updates to be visually smooth but mathematically controlled

                    let increment = 0;

                    if (progress < 30) {
                        // Phase 1: Initial Scan (Fast) - 0.8s
                        increment = 1.5 + Math.random();
                    } else if (progress < 70) {
                        // Phase 2: Processing Data (Steady) - 2.0s
                        increment = 0.4 + (Math.random() * 0.3);
                    } else if (progress < 98) {
                        // Phase 3: Analyzing Skin (Thoughtful) - 1.2s, Phase 4: Finalizing (Anticipation) - 1.0s
                        increment = 0.05 + (Math.random() * 0.05);
                    } else {
                        // CAP at 98% until manually completed by API success
                        clearInterval(container._progressInterval);
                        return;
                    }

                    progress = Math.min(98, progress + increment);

                    // Update UI
                    if (bar) bar.style.width = progress + '%';
                    if (text) text.textContent = Math.floor(progress);
                }
            }, 50); // 50ms interval = 20 ticks per second
        },

        completeProgressAnimation: function (container, onComplete) {
            if (!container) {
                if (onComplete) onComplete();
                return;
            }

            const bar = container.querySelector('.js-progress-bar');
            const text = container.querySelector('.js-progress-text');

            if (container._progressInterval) clearInterval(container._progressInterval);

            let progress = parseInt(text ? text.textContent : '98');
            const finishInterval = setInterval(() => {
                progress += 1;
                if (bar) bar.style.width = progress + '%';
                if (text) text.textContent = Math.floor(progress);

                if (progress >= 100) {
                    clearInterval(finishInterval);
                    setTimeout(() => {
                        if (onComplete) onComplete();
                    }, 500); // Give user a moment to see the 100%
                }
            }, 20);
        },

        showError: function (targetElement, message, buttonLabel, onClick) {
            if (!targetElement) return;
            targetElement.innerHTML = `
                <div style="text-align:center; padding: 30px;">
                    <p style="color:#ef4444;">${message}</p>
                    ${buttonLabel ? `<button class="welcome-btn primary" onclick="${onClick}" style="margin: 15px auto; padding: 8px 20px;">${buttonLabel}</button>` : ''}
                </div>`;
        },

        showToast: function (message, type = 'normal') {
            if (!DOM) this.refreshDOM();
            if (!DOM.box) return;

            const existing = document.querySelector('.chatbot-toast');
            if (existing) existing.remove();

            const toast = document.createElement('div');
            toast.className = `chatbot-toast ${type}`;

            let icon = '';
            if (type === 'error') icon = ICONS.TOAST_ERROR;
            else if (type === 'success') icon = ICONS.TOAST_SUCCESS;

            toast.innerHTML = `${icon}<span>${message}</span>`;
            DOM.box.appendChild(toast);

            requestAnimationFrame(() => toast.classList.add('visible'));
            setTimeout(() => {
                toast.classList.remove('visible');
                setTimeout(() => toast.remove(), 300);
            }, 4000);
        },

        /**
         * Triggered on window resize to ensure layout matches state
         */
        updateLayoutForState: function () {
            if (!DOM) this.refreshDOM();
            if (!isOpen) return;

            const currentWidth = window.innerWidth;
            // CRITICAL FIX: On mobile, scrolling triggers resize events because the address bar hides/shows.
            // This causes a height change but NOT a width change. We only want to refresh the layout
            // if the width has actually changed (e.g. orientation switch or desktop window resize).
            if (currentWidth === lastWindowWidth) {
                return;
            }
            lastWindowWidth = currentWidth;

            console.log("[ViewController] Width changed to " + currentWidth + ". Updating layout...");

            // If we are viewing results or guided chat, re-trigger the split logic (layout only, don't stop video)
            if (isViewingResults) {
                this.showGuidedChatView(true);
                if (window.ChatbotProducts && typeof window.ChatbotProducts.refreshDisplay === 'function') {
                    window.ChatbotProducts.refreshDisplay();
                }
            }

            // If screen becomes mobile-sized, ensure we are not "half-expanded"
            if (currentWidth < 1024 && isExpanded) {
                // We keep the internal state isExpanded, but the CSS will handle the mobile sizing.
            }
        }
    };

    window.ChatbotViewController = ViewController;

})();
